import './sentry'
import React from 'react'
import ReactDOM from 'react-dom/client'
import * as Sentry from '@sentry/react'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import './index.css'
import App from './App'
import Landing from './Landing'
import Wizard from './wizard/Wizard'
import AdminList from './admin/AdminList'
import AdminDetail from './admin/AdminDetail'
import AdminLogin from './admin/AdminLogin'
import AuditLog from './admin/AuditLog'
import ProtectedRoute from './components/ProtectedRoute'
import DesignReview from './design/DesignReview'

// Dev routes only loaded in development
const devRoutes = import.meta.env.DEV ? (() => {
  // Lazy-require dev components so they're tree-shaken from production
  const TestHandPain = React.lazy(() => import('./test-hand-pain'))
  const TestHandJoints = React.lazy(() => import('./TestHandJoints'))
  const TestPhotoCapture = React.lazy(() => import('./TestPhotoCapture'))
  const TestVideoCapture = React.lazy(() => import('./TestVideoCapture'))
  const TestVideoPalmDown = React.lazy(() => import('./test-video-palm-down'))
  const TestVideoPalmUp = React.lazy(() => import('./test-video-palm-up'))
  const VideoTest = React.lazy(() => import('./VideoTest'))
  const SimpleVideoTest = React.lazy(() => import('./SimpleVideoTest'))
  const DeveloperIndex = React.lazy(() => import('./DeveloperIndex'))
  const DeveloperDashboard = React.lazy(() => import('./components/DeveloperDashboard'))
  const WizardStepTester = React.lazy(() => import('./components/WizardStepTester'))
  const QuickTestDashboard = React.lazy(() => import('./components/QuickTestDashboard'))

  const wrap = (El: React.LazyExoticComponent<any>, props?: any) => (
    <React.Suspense fallback={<div className="p-4 text-center">Loading...</div>}>
      <El {...props} />
    </React.Suspense>
  )

  return [
    // Developer testing hub
    { path: '/developer', element: wrap(DeveloperIndex) },
    { path: '/quick-test', element: wrap(QuickTestDashboard) },

    // Wizard step testing system
    { path: '/dev-dashboard', element: wrap(DeveloperDashboard) },
    ...Array.from({ length: 23 }, (_, i) => ({
      path: `/wizard/step/${i + 1}`,
      element: wrap(WizardStepTester, { stepNumber: i + 1 })
    })),

    // Quick access to photo/video steps
    { path: '/photo/left-palm-down-spread', element: wrap(WizardStepTester, { stepNumber: 11 }) },
    { path: '/photo/left-palm-down-fist', element: wrap(WizardStepTester, { stepNumber: 12 }) },
    { path: '/photo/left-palm-up-spread', element: wrap(WizardStepTester, { stepNumber: 13 }) },
    { path: '/photo/left-palm-up-fist', element: wrap(WizardStepTester, { stepNumber: 14 }) },
    { path: '/photo/right-palm-down-spread', element: wrap(WizardStepTester, { stepNumber: 15 }) },
    { path: '/photo/right-palm-down-fist', element: wrap(WizardStepTester, { stepNumber: 16 }) },
    { path: '/photo/right-palm-up-spread', element: wrap(WizardStepTester, { stepNumber: 17 }) },
    { path: '/photo/right-palm-up-fist', element: wrap(WizardStepTester, { stepNumber: 18 }) },
    { path: '/video/left-palm-down-movement', element: wrap(WizardStepTester, { stepNumber: 19 }) },
    { path: '/video/left-palm-up-movement', element: wrap(WizardStepTester, { stepNumber: 20 }) },
    { path: '/video/right-palm-down-movement', element: wrap(WizardStepTester, { stepNumber: 21 }) },
    { path: '/video/right-palm-up-movement', element: wrap(WizardStepTester, { stepNumber: 22 }) },

    // Isolated test pages
    { path: '/test-hand-joints', element: wrap(TestHandJoints) },
    { path: '/test-photo-capture', element: wrap(TestPhotoCapture) },
    { path: '/test-video-capture', element: wrap(TestVideoCapture) },
    { path: '/video-test', element: wrap(VideoTest) },
    { path: '/simple-video-test', element: wrap(SimpleVideoTest) },
    { path: '/test-hand', element: wrap(TestHandPain) },
    { path: '/test-photo', element: wrap(TestPhotoCapture) },
    { path: '/test-video-palm-down', element: wrap(TestVideoPalmDown) },
    { path: '/test-video-palm-up', element: wrap(TestVideoPalmUp) },
  ]
})() : []

const router = createBrowserRouter([
  { path: '/', element: <Landing /> },
  { path: '/start', element: <App /> },
  { path: '/wizard', element: <Wizard /> },
  ...devRoutes,
  // Admin and design pages (always available)
  { path: '/design', element: <DesignReview /> },
  { path: '/admin/login', element: <AdminLogin /> },
  { path: '/admin', element: <ProtectedRoute><AdminList /></ProtectedRoute> },
  { path: '/audit_logs', element: <ProtectedRoute><AuditLog /></ProtectedRoute> },
  { path: '/admin/:id', element: <ProtectedRoute><AdminDetail /></ProtectedRoute> },
])

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Something went wrong</h2>
        <p>Please refresh the page or contact support if the problem persists.</p>
        <button onClick={() => window.location.reload()} style={{ marginTop: '1rem', padding: '0.5rem 1rem' }}>
          Refresh Page
        </button>
      </div>
    }>
      <RouterProvider router={router} />
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
)
